import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Category as CategoryIcon,
  Person as PersonIcon,
  Inventory as ProductIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import { TopesDescuentoStats, RubroResponse } from '../../types';

const TopesDescuentoDashboard: React.FC = () => {
  const [stats, setStats] = useState<TopesDescuentoStats | null>(null);

  // Consultar datos para el dashboard
  const { data: topesCliente = [], isLoading: loadingCliente } = useQuery({
    queryKey: ['topesCliente'],
    queryFn: () => apiService.obtenerTopesCliente(),
  });

  const { data: topesClienteRubro = [], isLoading: loadingRubro } = useQuery({
    queryKey: ['topesClienteRubro'],
    queryFn: () => apiService.obtenerTopesClienteRubro(),
  });

  const { data: topesClienteProducto = [], isLoading: loadingProducto } = useQuery({
    queryKey: ['topesClienteProducto'],
    queryFn: () => apiService.obtenerTopesClienteProducto(),
  });

  const { data: rubros = [], isLoading: loadingRubros } = useQuery({
    queryKey: ['rubros'],
    queryFn: () => apiService.obtenerRubrosDisponibles(),
  });

  // Calcular estadísticas
  useEffect(() => {
    if (!loadingCliente && !loadingRubro && !loadingProducto) {
      const fechaActual = new Date();
      const topesActivos = [
        ...topesCliente.filter(t => new Date(t.fechaVigencia) >= fechaActual && t.activo),
        ...topesClienteRubro.filter(t => new Date(t.fechaVigencia) >= fechaActual && t.activo),
        ...topesClienteProducto.filter(t => new Date(t.fechaVigencia) >= fechaActual && t.activo),
      ];
      
      const topesVencidos = [
        ...topesCliente.filter(t => new Date(t.fechaVigencia) < fechaActual || !t.activo),
        ...topesClienteRubro.filter(t => new Date(t.fechaVigencia) < fechaActual || !t.activo),
        ...topesClienteProducto.filter(t => new Date(t.fechaVigencia) < fechaActual || !t.activo),
      ];

      setStats({
        totalTopesCliente: topesCliente.length,
        totalTopesClienteRubro: topesClienteRubro.length,
        totalTopesClienteProducto: topesClienteProducto.length,
        topesActivos: topesActivos.length,
        topesVencidos: topesVencidos.length,
        rubrosDisponibles: rubros.length,
      });
    }
  }, [topesCliente, topesClienteRubro, topesClienteProducto, rubros, loadingCliente, loadingRubro, loadingProducto]);

  const isLoading = loadingCliente || loadingRubro || loadingProducto || loadingRubros;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (!stats) {
    return (
      <Alert severity="error">
        Error al cargar las estadísticas del dashboard
      </Alert>
    );
  }

  const totalTopes = stats.totalTopesCliente + stats.totalTopesClienteRubro + stats.totalTopesClienteProducto;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard de Topes de Descuento
      </Typography>

      {/* Tarjetas de estadísticas */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <PersonIcon color="primary" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {stats.totalTopesCliente}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Topes por Cliente
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <CategoryIcon color="secondary" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {stats.totalTopesClienteRubro}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Topes por Rubro
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center">
                <ProductIcon color="success" sx={{ mr: 2, fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" component="div">
                    {stats.totalTopesClienteProducto}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Topes por Producto
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Estado de los topes */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Estado de los Topes
              </Typography>
              <Box display="flex" alignItems="center" mb={2}>
                <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="body1">
                  Activos: {stats.topesActivos}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center">
                <WarningIcon color="warning" sx={{ mr: 1 }} />
                <Typography variant="body1">
                  Vencidos/Inactivos: {stats.topesVencidos}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Resumen Total
              </Typography>
              <Typography variant="h4" color="primary" gutterBottom>
                {totalTopes}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total de topes de descuento configurados
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default TopesDescuentoDashboard;
